/**
 * Device Authentication API
 *
 * API service for device registration, approval, and management.
 */

import { api } from './api';

export interface Device {
  _id: string;
  userId:
    | string
    | {
        _id: string;
        name?: string;
        email?: string;
        role?: string;
      };
  deviceToken: string;
  fingerprint: string;
  registeredName: string;
  registeredPhone: string;
  deviceName: string;
  browser?: string;
  os?: string;
  registrationIp?: string;
  lastIp?: string;
  registrationLocation?: string;
  lastLocation?: string;
  status: 'pending' | 'active' | 'inactive' | 'revoked';
  approvedBy?: {
    _id: string;
    name?: string;
    email?: string;
  };
  approvedAt?: string;
  deactivatedBy?: {
    _id: string;
    name?: string;
    email?: string;
  };
  deactivatedAt?: string;
  deactivationReason?: string;
  lastUsedAt: string;
  expiresAt?: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  canLogin?: boolean;
}

export interface DeviceStats {
  total: number;
  pending: number;
  active: number;
  inactive: number;
  revoked: number;
}

export interface DeviceCheckResult {
  isKnown: boolean;
  status?: 'pending' | 'active' | 'inactive' | 'revoked';
  message?: string;
  device?: Device;
}

export interface DeviceRegistrationResult {
  success: boolean;
  deviceToken?: string;
  device?: Device;
  message: string;
}

export const devicesApi = {
  /**
   * Check if a device is known (called during login flow)
   */
  checkDevice: async (
    userId: string,
    fingerprint: string,
    deviceToken?: string
  ): Promise<DeviceCheckResult> => {
    const response = await api.post('/devices/check', {
      userId,
      fingerprint,
      deviceToken,
    });
    return response.data;
  },

  /**
   * Register a new device (called when new device detected)
   */
  registerDevice: async (data: {
    userId: string;
    fingerprint: string;
    registeredName: string;
    registeredPhone: string;
    deviceName?: string;
    browser?: string;
    os?: string;
    organizationId: string;
  }): Promise<DeviceRegistrationResult> => {
    const response = await api.post('/devices/register', data);
    return response.data;
  },

  /**
   * Get current user's devices
   */
  getMyDevices: async (): Promise<Device[]> => {
    const response = await api.get('/devices/my-devices');
    return response.data.devices;
  },

  /**
   * Get pending devices count (for notification badge)
   */
  getPendingCount: async (): Promise<number> => {
    const response = await api.get('/devices/pending-count');
    return response.data.count;
  },

  /**
   * Get device statistics for the organization
   */
  getStats: async (): Promise<DeviceStats> => {
    const response = await api.get('/devices/stats');
    return response.data.stats;
  },

  /**
   * Get all devices for the organization (admin view)
   */
  getAllDevices: async (filters?: {
    status?: string;
    userId?: string;
  }): Promise<Device[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.userId) params.append('userId', filters.userId);

    const response = await api.get(`/devices?${params.toString()}`);
    return response.data.devices;
  },

  /**
   * Get a specific device
   */
  getDevice: async (deviceId: string): Promise<Device> => {
    const response = await api.get(`/devices/${deviceId}`);
    return response.data.device;
  },

  /**
   * Approve a pending device
   */
  approveDevice: async (
    deviceId: string,
    expiresInDays?: number
  ): Promise<{ success: boolean; device: Device; message: string }> => {
    const response = await api.post(`/devices/${deviceId}/approve`, {
      expiresInDays,
    });
    return response.data;
  },

  /**
   * Deactivate a device
   */
  deactivateDevice: async (
    deviceId: string,
    reason?: string
  ): Promise<{ success: boolean; device: Device; message: string }> => {
    const response = await api.post(`/devices/${deviceId}/deactivate`, {
      reason,
    });
    return response.data;
  },

  /**
   * Reactivate an inactive device
   */
  reactivateDevice: async (
    deviceId: string,
    expiresInDays?: number
  ): Promise<{ success: boolean; device: Device; message: string }> => {
    const response = await api.post(`/devices/${deviceId}/reactivate`, {
      expiresInDays,
    });
    return response.data;
  },

  /**
   * Revoke a device permanently
   */
  revokeDevice: async (
    deviceId: string,
    reason: string
  ): Promise<{ success: boolean; device: Device; message: string }> => {
    const response = await api.post(`/devices/${deviceId}/revoke`, {
      reason,
    });
    return response.data;
  },

  /**
   * Delete a device
   */
  deleteDevice: async (
    deviceId: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/devices/${deviceId}`);
    return response.data;
  },

  /**
   * Bulk approve devices
   */
  bulkApproveDevices: async (
    deviceIds: string[],
    expiresInDays?: number
  ): Promise<{ success: boolean; approvedCount: number; message: string }> => {
    const response = await api.post('/devices/bulk-approve', {
      deviceIds,
      expiresInDays,
    });
    return response.data;
  },
};
